import React, { useRef, useEffect } from "react";
import { View, Text, TouchableOpacity } from "react-native";
import Colors from "../../../../Resources/Color";
import { getScrollableNodeFromRef } from "../utils/floatingUtils";
import { HighlightedText } from "./HighlightedText";
import { PILL_COLORS } from "../constants";

/**
 * Workout Library Row component for displaying workout results from the library
 */
export const WorkoutLibraryRow = ({
  result,
  isSelected,
  onSelect,
  onHover,
  searchQuery,
  t,
  language,
  index,
  totalResults,
  scrollContainerRef,
  keyboardNavigationRef,
}) => {
  const rowRef = useRef(null);

  const exercises = result.workout?.exercises || [];
  const displayedExercises = exercises.slice(0, 3);
  const remainingCount = Math.max(0, exercises.length - 3);

  // Scroll into view when selected
  useEffect(() => {
    if (
      isSelected &&
      keyboardNavigationRef?.current &&
      rowRef.current &&
      scrollContainerRef?.current
    ) {
      const isFirstItem = index === 0;
      const isLastItem = index === totalResults - 1;
      const rowElement = rowRef.current;

      const scrollContainer = getScrollableNodeFromRef(scrollContainerRef);

      if (isFirstItem) {
        scrollContainerRef.current?.scrollTo?.({ y: 0, animated: true });
        if (scrollContainer) {
          scrollContainer.scrollTop = 0;
        }
        return;
      }

      if (isLastItem) {
        scrollContainerRef.current?.scrollToEnd?.({ animated: true });
        if (scrollContainer) {
          const containerHeight = scrollContainer.clientHeight;
          const scrollHeight = scrollContainer.scrollHeight;
          scrollContainer.scrollTop = scrollHeight - containerHeight;
        }
        return;
      }

      rowElement.scrollIntoView?.({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [
    isSelected,
    index,
    totalResults,
    scrollContainerRef,
    keyboardNavigationRef,
  ]);

  return (
    <TouchableOpacity
      ref={rowRef}
      onPress={onSelect}
      onMouseEnter={onHover}
      style={{
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "space-between",
        paddingVertical: 10,
        paddingHorizontal: 12,
        marginHorizontal: 8,
        borderRadius: 10,
        backgroundColor: isSelected
          ? "rgba(200, 140, 220, 0.12)"
          : "transparent",
        outlineStyle: "none",
      }}
    >
      {/* Left side: workout name + exercise preview */}
      <View
        style={{
          flex: 1,
          minWidth: 0,
          marginRight: 10,
        }}
      >
        {/* Workout name with highlighting */}
        <HighlightedText text={result.workoutName} searchQuery={searchQuery} />

        {/* Exercise previews */}
        <View style={{ marginTop: 6, gap: 2 }}>
          {displayedExercises.map((ex, idx) => {
            const exerciseName = ex.getName
              ? ex.getName(t, language)
              : ex.name || ex.originalName || "";
            const setCount = ex.series?.length || 0;

            return (
              <View
                key={ex.id || idx}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Text
                  style={{
                    fontSize: 11,
                    color: Colors.grayColor,
                    flex: 1,
                  }}
                  numberOfLines={1}
                >
                  {exerciseName}
                </Text>
                <Text
                  style={{
                    fontSize: 10,
                    color: Colors.grayColor,
                    fontWeight: "500",
                  }}
                >
                  {setCount} {setCount === 1 ? t("setSingular-string") : t("sets-string")}
                </Text>
              </View>
            );
          })}

          {/* +X exercises indicator */}
          {remainingCount > 0 && (
            <Text
              style={{
                fontSize: 10,
                color: Colors.grayColor,
                fontStyle: "italic",
                marginTop: 2,
              }}
            >
              +{remainingCount} {remainingCount === 1 ? t("exercise-string") : t("exercises-string")}
            </Text>
          )}
        </View>
      </View>

      {/* Right side: Library pill */}
      <View
        style={{
          backgroundColor: PILL_COLORS.library.background,
          paddingHorizontal: 8,
          paddingVertical: 3,
          borderRadius: 8,
          alignSelf: "flex-start",
        }}
      >
        <Text
          style={{
            fontSize: 11,
            fontWeight: "500",
            color: PILL_COLORS.library.text,
          }}
        >
          {t("libraryWorkout-string")}
        </Text>
      </View>
    </TouchableOpacity>
  );
};
